import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DEFAULT_URL } from "../../app-urls";
import { Subscription } from "rxjs";
import { RepositoryService } from "../../linq-utils/repository.service";
import { UserService } from "../../user/user.service";
import { SCREEN } from "../layouts-enums";
import { UIComponentsModule } from "../../ui-components/ui-components.module";
import { BILLING_STATUS, BillingAmount, BillingRunSummary, getBillingStatusDescription, OrganisationBilling } from "../../common/organisation";
import { MaterialModule } from "../../material/material.module";
import { BillingRecordsTableComponent } from "../../ui-components/billing-records/billing-records-table.component";
import { MODEL_COLOURS } from "../../model-services/model-item-enums";
import { BillingRunTableComponent } from "./billing-run-table.component";
import { BarData } from "../../charts/bar-chart/bar-chart.component";
import { ChartsModule } from "../../charts/charts.module";
import { DownloadService } from "../../model-services/download.service";

const COMPONENT = "Billing admin"
const url = "api/billing/runs"

class CostCard {
    constructor(public text:string, public billingAmount:BillingAmount, public icon?:string) {}
}

@Component({
    selector: 'app-billing-admin',
    templateUrl: './billing-admin.html',
    styleUrls: ['./billing-admin.scss'],
    standalone: true,
    imports: [MaterialModule, UIComponentsModule, ChartsModule, BillingRecordsTableComponent, BillingRunTableComponent]
})
export class BillingAdminComponent implements OnInit, OnDestroy {
    screen:SCREEN = SCREEN.BILLING_ADMIN
    loading:boolean
    routeSub:Subscription

    selectedRunId:number
    billingRuns:BillingRunSummary[]
    cards:CostCard[]
    chartData:BarData[]

    billingRecords:OrganisationBilling[]
   
    constructor(private route:ActivatedRoute, private router:Router, private repoService:RepositoryService,
        private userService:UserService, private downloadService:DownloadService) {
    }

    ngOnDestroy(): void {
        if (this.routeSub) this.routeSub.unsubscribe()
    }

    ngOnInit() {
        setTimeout(() => {

            this.routeSub = this.route.params.subscribe(async p => {
                this.loadAdminData()
            })
        })
    }

    private async loadAdminData() {
        this.loading = true

        try {           
            const SERVICE = "Load admin data"
            this.billingRuns = await this.repoService.callSingleAPI(this.repoService.getData(url), COMPONENT, SERVICE, this.userService.getUser())
            this.createChartData()
        }catch(err) {
            this.router.navigateByUrl(DEFAULT_URL())
        }finally {
            this.loading = false
        }
    }

    // {runId:runId, amountPaid:0, amountUnpaid:0, nodes:0, projects:0, users:0, nodesCost:0, projectsCost:0, usersCost:0, organisations:0, creditcard:0, invoiced:0, exempt:0, unpaid:0})
    private createChartData() {
        if (this.billingRuns && this.billingRuns.length > 0) {
            this.chartData = this.billingRuns.sort((a,b) => a.runId>b.runId?1:-1).map(bd => {
                return new BarData(bd.runId.toString(), bd.runId.toString(), [bd.nodes.amount, bd.projects.amount, bd.users.amount], [MODEL_COLOURS.BILLING_NODES, MODEL_COLOURS.BILLING_PROJECTS, MODEL_COLOURS.BILLING_USERS], [MODEL_COLOURS.BILLING_HOVER, MODEL_COLOURS.BILLING_HOVER, MODEL_COLOURS.BILLING_HOVER])
            })
        }
    }

    async handleBarSelection(runId:string) {
        const runIdNum = (new Number(runId)).valueOf()
        this.handleBarSelectionNumber(runIdNum)
    }

    async handleBarSelectionNumber(runId:number) {
        this.selectedRunId = runId
        this.billingRecords = await this.repoService.callSingleAPI(this.repoService.getData(url + "/" + runId), COMPONENT, "Select run", this.userService.getUser())

        this.cards = []
        const data = this.billingRuns.find(bd => bd.runId === this.selectedRunId)

        this.cards.push(new CostCard("Organisations", data.organisations))
        this.cards.push(new CostCard("Nodes", data.nodes))
        this.cards.push(new CostCard("Projects", data.projects))
        this.cards.push(new CostCard("Users", data.users))

        Object.keys(data.states).forEach(s => {
            const status = parseInt(s) as BILLING_STATUS
            this.cards.push(new CostCard(getBillingStatusDescription(status), data.states[status]))
        })
    }

    async createBillingRun() {
        await this.repoService.callSingleAPI(this.repoService.create(url, {}), COMPONENT, "Create billing run", this.userService.getUser())
        this.loadAdminData()
    }

    async executeBillingRun() {
        await this.repoService.callSingleAPI(this.repoService.update(url + "/" + this.selectedRunId, {}), COMPONENT, "Execute billing run", this.userService.getUser())
        this.loadAdminData()
    }

    async checkBillingRun() {
        await this.repoService.callSingleAPI(this.repoService.getData(url + "/" + this.selectedRunId + "/checkstatus"), COMPONENT, "Check billing run", this.userService.getUser())
        this.loadAdminData()
    }


    exportRuns() {
        if (this.billingRuns) this.downloadService.createBillingRunCSV(this.billingRuns)
    }

    exportRun() {
        if (this.billingRecords) this.downloadService.createOrgBillingCSV(this.billingRecords)            
    }
}
